import * as React from 'react';
import { connect } from 'react-redux';

import {
  mediaEvent,
  playerError,
  timeUpdate,
} from '../actions';
import { RootState } from '../types';

export type IMediaEvent =
  'abort' |
  'canplay' |
  'canplaythrough' |
  'durationchange' |
  'emptied' |
  'encrypted' |
  'ended' |
  'error' |
  'loadeddata' |
  'loadedmetadata' |
  'loadstart' |
  'pause' |
  'play' |
  'playing' |
  'progress' |
  'ratechange' |
  'seeked' |
  'seeking' |
  'stalled' |
  'suspend' |
  'timeupdate' |
  'volumechange' |
  'waiting';


export interface ISeekTarget {
  time: number,
  timeRelative: boolean,
  isPlaying: boolean,
}

export interface IProps {
  onMediaEvent: (e: IMediaEvent) => void,
  onTimeUpdate: (time: number) => void,
  onError: (error: MediaError) => void,
  playbackRate: number,
  src: string | undefined,
  seekTarget: ISeekTarget | null,
}

export class Player extends React.Component<IProps> {

  private audio: HTMLAudioElement | null = null;
  private timer: NodeJS.Timer | null = null;

  public componentDidMount() {
    const {audio} = this;
    if (!audio) {
      throw new Error("audio element failed to mount");
    }
  }

  public render() {
    const {
      src,
    } = this.props;

    return (<audio
      ref={this.onRef}
      src={src}
      autoPlay={true}
      onAbort={this.onMediaEvent}
      onCanPlay={this.onMediaEvent}
      onCanPlayThrough={this.onMediaEvent}
      onDurationChange={this.onMediaEvent}
      onEmptied={this.onMediaEvent}
      onEncrypted={this.onMediaEvent}
      onEnded={this.onMediaEvent}
      onError={this.onError}
      onLoadedData={this.onLoadedData}
      onLoadedMetadata={this.onMediaEvent}
      onLoadStart={this.onMediaEvent}
      onPause={this.onMediaEvent}
      onPlay={this.onMediaEvent}
      onPlaying={this.onMediaEvent}
      onProgress={this.onMediaEvent}
      onRateChange={this.onMediaEvent}
      onSeeked={this.onMediaEvent}
      onSeeking={this.onMediaEvent}
      onStalled={this.onMediaEvent}
      onSuspend={this.onMediaEvent}
      onVolumeChange={this.onMediaEvent}
      onWaiting={this.onMediaEvent}
      crossOrigin="anonymous" />);
  }

  public componentDidUpdate(prevProps: IProps) {
    const {
      playbackRate,
      seekTarget,
    } = this.props;
    const {audio} = this;

    if (seekTarget && prevProps.seekTarget !== seekTarget) {
      this.seek(seekTarget);
    }
    if (audio && playbackRate !== prevProps.playbackRate) {
      audio.playbackRate = playbackRate;
    }
  }

  public seek(target: ISeekTarget) {
    const {audio} = this;
    if (!audio) { return; }

    const {
      isPlaying,
      time,
      timeRelative,
    } = target;

    if (timeRelative) {
      audio.currentTime += time
    } else {
      audio.currentTime = time
    }

    if (isPlaying) {
      audio.play()
    } else {
      audio.pause();
    }
  }

  public componentWillMount() {
    this.timer = setInterval(this.onTimeUpdate, 33);
  }

  public componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private onRef = (ref: HTMLAudioElement|null) => {
    this.audio = ref;
  }

  private onLoadedData = (e: React.SyntheticEvent) => {
    const {audio} = this;
    const {
      playbackRate,
      seekTarget,
    } = this.props;
  

    // HACK(maxhawkins): not sure this actually works. What if the seek
    // was intended for a previous recording? Need to test.
    if (audio && seekTarget && !seekTarget.timeRelative) {
      audio.currentTime = seekTarget.time;
    }
    if (audio) {
      audio.playbackRate = playbackRate;
    }

    this.props.onMediaEvent(e.type as IMediaEvent);
  }

  private onMediaEvent = (e: React.SyntheticEvent) => {
    this.props.onMediaEvent(e.type as IMediaEvent);
  }

  private onTimeUpdate = () => {
    const {audio, props} = this;
    if (!audio) { return; }

    props.onTimeUpdate(audio.currentTime);
  }

  private onError = (e: React.SyntheticEvent) => {
    const {audio, props} = this;

    if (!audio || !audio.error) { return; }

    props.onError(audio.error);
  }

}

function getPlayingSrc({player, tracks}: RootState): string | undefined {
  const {recordingId} = player;
  if (!recordingId) {
    return;
  }

  const track = tracks.data[recordingId];
  if (!track) {
    return;
  }

  return track.url;
}

export const PlayerContainer = connect(
  (state: RootState) => ({
    playbackRate: state.player.playbackRate,
    seekTarget: state.player.seekTarget,
    src: getPlayingSrc(state),
  }),
  dispatch => ({
    onError: (error: MediaError) => {
      // tslint:disable-next-line
      console.warn(error.message);
      dispatch(playerError(error))
    },
    onMediaEvent: (e: IMediaEvent) => { dispatch(mediaEvent(e)) },
    onTimeUpdate: (time: number) => { dispatch(timeUpdate(time)) },
  }),
)(Player);
export default PlayerContainer;


// WEBPACK FOOTER //
// ./src/lib/AudiostorePlayer.tsx